import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { Archive } from "lucide-react";

// Mirrors property_management.Property.WITHDRAWN_REASON_APPRAISAL_CHOICES /
// WITHDRAWN_REASON_LISTING_CHOICES - the backend re-validates against
// reason_choices_for_status() on save, this is just what's offered to pick from.
const APPRAISAL_REASONS = [
    { value: "lost_to_other_agent", label: "Lost to other agent" },
    { value: "owner_not_proceeding", label: "Owner not proceeding" },
];

const LISTING_REASONS = [
    { value: "agreement_expired", label: "Agreement expired" },
    { value: "owner_withdrew", label: "Owner withdrew" },
    { value: "sold_by_another_agent", label: "Sold by another agent" },
    { value: "leased_by_another_agent", label: "Leased by another agent" },
    { value: "duplicate", label: "Duplicate" },
    { value: "error", label: "Error" },
    { value: "listed_with_another_agent", label: "Listed with another agent" },
    { value: "other", label: "Other" },
];

/**
 * Withdraw reconfirm dialog. Which reason list (and body copy) is shown
 * depends on the property's CURRENT status - an Appraisal-stage property
 * was never on the public market, so its reasons differ from a live
 * On/Off Market Listing being pulled.
 */
const WithdrawPropertyModal = ({ isOpen, onClose, onConfirm, loading = false, statusName }) => {
    const isAppraisal = (statusName || "").trim().toLowerCase() === "appraisal";
    const reasons = isAppraisal ? APPRAISAL_REASONS : LISTING_REASONS;
    const description = isAppraisal
        ? "I wish to withdraw this appraisal"
        : "I wish to withdraw this listing from the market (the listing authority has expired or a mutual decision with the owner)";

    const [reason, setReason] = useState("");
    const [reasonNote, setReasonNote] = useState("");

    useEffect(() => {
        if (isOpen) {
            setReason("");
            setReasonNote("");
        }
    }, [isOpen, statusName]);

    const isOther = reason === "other";
    const canContinue = !loading && !!reason && (!isOther || reasonNote.trim().length > 0);

    const handleConfirm = () => {
        if (!canContinue) return;
        onConfirm(reason, isOther ? reasonNote.trim() : "");
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px] rounded-2xl border-none shadow-2xl">
                <DialogHeader>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-orange-100 text-orange-600">
                            <Archive className="w-6 h-6" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
                                Reconfirm
                            </DialogTitle>
                        </div>
                    </div>
                </DialogHeader>

                <div className="py-4 space-y-4">
                    <DialogDescription className="text-sm font-medium text-gray-500 leading-relaxed">
                        {description}
                    </DialogDescription>

                    <div>
                        <label className="text-xs font-bold text-[#374151] block mb-1">
                            Reason
                        </label>
                        <Select value={reason} onValueChange={setReason} disabled={loading}>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select a reason" />
                            </SelectTrigger>
                            <SelectContent>
                                {reasons.map((r) => (
                                    <SelectItem key={r.value} value={r.value}>
                                        {r.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {isOther && (
                        <div>
                            <label className="text-xs font-bold text-[#374151] block mb-1">
                                Please specify
                            </label>
                            <textarea
                                autoFocus
                                value={reasonNote}
                                onChange={(e) => setReasonNote(e.target.value)}
                                rows={3}
                                disabled={loading}
                                placeholder="Add a short detail..."
                                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#55C5D0]"
                            />
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-3 sm:gap-0 sm:flex-row flex-col">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-gray-500 bg-gray-100 border border-transparent rounded-xl hover:bg-gray-200 transition-all font-sans"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!canContinue}
                        className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-white rounded-xl transition-all font-sans shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 focus:ring-orange-500"
                    >
                        {loading ? (
                            <>
                                <span className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                Processing...
                            </>
                        ) : (
                            "Continue"
                        )}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default WithdrawPropertyModal;
